<script lang="ts">
    import { urlsBuilderContextToken } from "@/context_tokens";
    import { GiftCartDrawerWidgetDefinition } from "@apps/gift_registry/definitions";
    import { GiftRegistryItemEntity } from "@apps/gift_registry/entities/GiftRegistryItemEntity";
    import { GiftRegistryWithItemsEntity } from "@apps/gift_registry/entities/GiftRegistryWithItemsEntity";
    import GiftCartService from "@apps/gift_registry/services/GiftCartService";
    import WishlistPageItemSkeleton from "@apps/wishlist/widgets/wishlist_page_widget/components/WishlistPageItemSkeleton.svelte";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import CN from "classnames";
    import debounce from "debounce";
    import { map } from "rxjs";
    import { createEventDispatcher } from "svelte";

    import ActionItem from "@components/Actions/ActionItem.svelte";
    import ActionItemLabel from "@components/Actions/ActionItemLabel.svelte";
    import ActionsList from "@components/Actions/ActionsList.svelte";
    import ActionsPopover from "@components/Actions/ActionsPopover.svelte";
    import { BOTTOM_RIGHT } from "@components/Actions/positions";
    import Badge from "@components/Badge/Badge.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import ButtonWithIcon from "@components/Buttons/ButtonWithIcon.svelte";
    import Card from "@components/Card/Card.svelte";
    import DrawerManager from "@components/Drawer/DrawerManager";
    import Icon from "@components/Icon.svelte";
    import DecreaseIcon from "@components/icons/DecreaseIcon.svelte";
    import IncreaseIcon from "@components/icons/IncreaseIcon.svelte";
    import ThreedotVerticalIcon from "@components/icons/ThreedotVerticalIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";
    import T from "@components/T.svelte";

    export let registry: GiftRegistryWithItemsEntity;
    export let registries: GiftRegistryWithItemsEntity[];
    export let item: GiftRegistryItemEntity;
    export let productsQuery: ProductsQuery;
    export let giftCartService: GiftCartService | null = null;
    export let drawerManager: DrawerManager | null = null;
    export let dynamicImportManager: DynamicImportManager;
    export let isSharedItem = false;
    export let namespace: string;

    const dispatch = createEventDispatcher();

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);

    let quantity = isSharedItem ? 1 : item.quantity;

    const handleAddToCart = async () => {
        await dynamicImportManager.fetch({
            definition: GiftCartDrawerWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it
        setTimeout(() => {
            giftCartService?.addItem(item, quantity);
            drawerManager?.drawerShow();
        }, 0);
    };

    const targetRegistries = registries.filter((registry) =>
        registry.items.every(
            (currentItem) => currentItem.productId != item.productId
        )
    );

    //TODO: попробовать вариант с ProductContainer (Zhavlon)
    const productAndVariantInfo$ = productsQuery.selectProductAndVariant(
        item.productId,
        item.variantId
    );

    const isProductPurchased =
        isSharedItem && item.purchasedAmount >= item.quantity;

    const productUrl$ = productAndVariantInfo$.pipe(
        map((productInfo) => {
            if (!productInfo.product) return null;

            return urlsBuilder.createProductUrl(
                productInfo.product.handle,
                productInfo.variant?.id
            );
        })
    );

    const imageUrl$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            const [variantImage, productImage] = [
                productAndVariantInfo?.variant?.image?.url,
                productAndVariantInfo?.product?.featuredImage?.url,
            ];
            return variantImage || productImage;
        })
    );

    const isProductOnSale$ = productAndVariantInfo$.pipe(
        map((productAndVariantInfo) => {
            return !!(
                productAndVariantInfo.variant?.compareAtPrice &&
                productAndVariantInfo.variant?.compareAtPrice.amount !==
                    productAndVariantInfo.variant?.price.amount
            );
        })
    );

    const productOptions$ = productAndVariantInfo$.pipe(
        map((productInfo) =>
            productInfo.variant?.options.filter(
                (option) => option.value !== "Default Title"
            )
        )
    );

    const handleMoveItem = (toGiftRegistryId: number) => {
        dispatch("moveItem", {
            giftRegistryItemId: item.id,
            fromGiftRegistryId: registry.id,
            toGiftRegistryId,
        });
    };

    const handleDeleteItem = () => {
        dispatch("deleteItem", {
            productId: item.productId,
            itemId: item.id,
            registryId: registry.id,
        });
    };

    const debouncedDispatch = debounce(() => {
        dispatch("updateQuantity", {
            giftRegistryItem: {
                giftRegistryId: registry.id,
                productId: item.productId,
                variantId: item.variantId,
                quantity: quantity,
                id: item.id,
            },
        });
    }, 500);

    const handleQuantityChange = (value: number | string, delta: number) => {
        const newValue = +value + delta;
        if (newValue > 0) {
            quantity = newValue;
        }

        if (!isSharedItem) {
            debouncedDispatch();
        }
    };
</script>

{#if !$productAndVariantInfo$.product}
    <Card>
        <WishlistPageItemSkeleton />
    </Card>
{:else}
    <div class="gw-gr-page-item">
        {#if !isSharedItem}
            <div class="gw-gr-page-item__actions">
                <ActionsPopover position={BOTTOM_RIGHT}>
                    <div slot="activator">
                        <ButtonWithIcon>
                            <Icon color="#B1B5C3">
                                <ThreedotVerticalIcon />
                            </Icon>
                        </ButtonWithIcon>
                    </div>
                    <ActionsList>
                        <ActionItem color="#EF466F" on:click={handleDeleteItem}>
                            <span class="gw-gr-action-item">
                                <T {namespace} translationKey="delete_item" />
                            </span>
                        </ActionItem>
                        {#if targetRegistries.length}
                            <ActionItemLabel>
                                <T {namespace} translationKey="move" />
                            </ActionItemLabel>
                            {#each targetRegistries as registry}
                                <ActionItem
                                    on:click={() => handleMoveItem(registry.id)}
                                >
                                    {registry.title}
                                </ActionItem>
                            {/each}
                        {/if}
                    </ActionsList>
                </ActionsPopover>
            </div>
        {/if}
        <a href={$productUrl$} class="gw-gr-page-item__image">
            <Image
                objectFit="contain"
                ratio={1 / 1}
                src={urlsBuilder.createProductImageUrl($imageUrl$ || "")}
                alt={$productAndVariantInfo$.product?.title || ""}
            />
            {#if isProductPurchased}
                <div class="gw-gr-page-item__badge">
                    <Badge
                        variant="info"
                        textColor="#fff"
                        backgroundColor="#45B26B"
                    >
                        <T {namespace} translationKey="purchased_badge_label" />
                    </Badge>
                </div>
            {/if}
        </a>
        <div class="gw-gr-page-item__header">
            <a href={$productUrl$} class="gw-gr-page-item__title">
                {$productAndVariantInfo$.product?.title}
            </a>

            <div class="gw-gr-page-item__price-block">
                {#if $isProductOnSale$}
                    <div class="gw-gr-page-item__compare-price">
                        <Money
                            with_currency
                            amount={$productAndVariantInfo$?.variant
                                ?.compareAtPrice?.amount || 0}
                        />
                    </div>
                {/if}

                <div class="gw-gr-page-item__price">
                    <Money
                        with_currency
                        amount={$productAndVariantInfo$?.variant?.price
                            .amount || 0}
                    />
                </div>
            </div>
        </div>
        {#if $productOptions$?.length}
            <div class="gw-gr-page-item__content">
                <table class="gw-gr-page-item__options">
                    {#each $productOptions$ as option}
                        <tr class="gw-gr-page-item__options-item">
                            <td class="gw-gr-page-item__options-item-name">
                                {option.name}:
                            </td>
                            <td class="gw-gr-page-item__options-item-value">
                                {option.value}
                            </td>
                        </tr>
                    {/each}
                </table>
            </div>
        {/if}
        <div class="gw-gr-page-item__quantity">
            <div class="gw-gr-page-item__quantity-info">
                <div>
                    <T {namespace} translationKey="item_quantity_label" />
                    <span>{item.quantity}</span>
                </div>
                <div>
                    <T
                        {namespace}
                        translationKey="item_purchased_amount_label"
                    />
                    <span>{item.purchasedAmount}</span>
                </div>
            </div>
        </div>
        <div
            class={CN("gw-gr-page-item__action-btn", {
                "--disabled": isProductPurchased,
            })}
        >
            <ButtonWithIcon
                class="gw-gr-page-item__counter-btn"
                on:click={() => handleQuantityChange(quantity, -1)}
            >
                <Icon width={24} height={24} viewBox="0 0 24 24">
                    <DecreaseIcon />
                </Icon>
            </ButtonWithIcon>
            {#if !isSharedItem}
                <span class="gw-gr-page-item__quantity-text">
                    {quantity}
                </span>
            {:else}
                <Button
                    class="gw-gr-page-item__quantity-btn"
                    on:click={handleAddToCart}
                    variant="text"
                    size="large"
                >
                    <T
                        {namespace}
                        translationKey="add_to_cart"
                        translationOptions={{
                            quantity,
                        }}
                    />
                </Button>
            {/if}
            <ButtonWithIcon
                class="gw-gr-page-item__counter-btn"
                on:click={() => handleQuantityChange(quantity, +1)}
            >
                <Icon width={24} height={24} viewBox="0 0 24 24">
                    <IncreaseIcon />
                </Icon>
            </ButtonWithIcon>
        </div>
    </div>
{/if}

<style lang="postcss">
    @import "../../../../../themes/globals.css";
    @import "../../../../../themes/default/default.css";

    .gw-gr-page-item {
        position: relative;
        height: 100%;

        &__header {
            margin-top: 12px;
            display: flex;
            align-items: start;
            justify-content: space-between;
        }

        &__title {
            display: -webkit-box;
            font-size: 0.875em;
            line-height: 1.5em;
            color: inherit;
            text-decoration: none;
            display: flex;
            gap: 10px;
            flex-direction: column;

            &-info {
                display: flex;
                gap: 10px;

                span {
                    margin-left: 3px;
                }
            }
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        &__price-block {
            display: flex;
            flex-wrap: wrap;
            gap: 3px;
            justify-content: center;
            align-items: center;
            line-height: 0;
            margin: 0.12em 0 0.3em 0;
        }

        &__compare-price {
            display: inline-block;
            font-size: 0.625em;
            line-height: 0.75em;
            text-decoration: line-through;
            color: #b1b5c3;
        }

        &__price {
            font-size: 0.75em;
            font-weight: 600;
            line-height: 0.75em;
            padding: 0.25em 0.375em;
            display: inline-block;
            border-radius: 0.25em;
            color: #45b26b;
            border: 1px solid rgba(69, 178, 107, 0.4);
        }

        &__image {
            --gw-image-backdrop-color: transparent;
            position: relative;
        }

        &__badge {
            position: absolute;
            left: 8px;
            bottom: 8px;
            display: flex;
            gap: 5px;
            flex-direction: column;
            :global(.gw-badge) {
                width: fit-content;
                padding: 2px 8px;
                border-radius: 10px;
                line-height: 12px;
            }
        }

        &__actions {
            position: absolute;
            top: 0;
            left: 10px;
            z-index: 99;
        }

        &__options {
            border-spacing: 0;
            margin: 5px 0 10px 0;

            &-item {
                &-name {
                    color: #777e90;
                    font-size: 0.6em;
                    font-weight: 500;
                    padding: 0 6px 0 0;
                }
                &-value {
                    color: #141416;
                    font-size: 0.6em;
                    font-weight: 500;
                    padding: 0;
                }
            }
        }
        &__quantity {
            display: flex;
            gap: 10px;
            flex-direction: column;
            font-size: 0.7em;

            &-info {
                display: flex;
                gap: 10px;

                span {
                    margin-left: 3px;
                }
            }
            &-change {
                display: flex;
                border: 1px solid #000;
                padding: 5px 10px;
                border-radius: 5px;

                &.--gw-with-error {
                    border: 3px solid red;
                }

                span {
                    cursor: pointer;
                }
            }

            &-text {
                font-size: 14px;
                font-style: normal;
                font-weight: 700;
                line-height: 16px;
            }
        }
        &__action-btn {
            margin-top: 0.7em;
            display: flex;
            padding: 5px;
            align-items: center;
            justify-content: space-between;
            border: 1px solid var(--gw-lightgrey-text-color);
            border-radius: 90px;
            &.--disabled {
                opacity: 0.4;
                pointer-events: none;
            }
        }

        :global(.gw-btn.gw-gr-page-item__counter-btn) {
            border-radius: 90px;
        }
        :global(.gw-btn.gw-button.gw-gr-page-item__quantity-btn) {
            padding: 5px 10px;
            flex: 1;
            font-weight: 700;
        }
    }
</style>
